import React from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

export interface DeleteConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    /** The type of item being deleted e.g. "template", "matter", "contact" */
    itemType?: string;
    /** The name/label of the specific item being deleted */
    itemName?: string;
    /** Override the default title */
    title?: string;
    /** Override the default description */
    description?: string;
    /** Override the confirm button label */
    confirmLabel?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            borderRadius: 16,
            maxWidth: 420,
            width: '100%',
            overflow: 'hidden',
        },

        header: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: theme.spacing(1.5, 1.5, 0),
        },

        closeBtn: {
            color: theme.palette.text.disabled,
            padding: 6,
            '&:hover': {
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.grey[100],
            },
        },

        content: {
            padding: theme.spacing(0, 4, 3),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: theme.spacing(1.5),
        },

        iconWrapper: {
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#fff1f1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: theme.spacing(0.5),
        },

        icon: {
            fontSize: 32,
            color: theme.palette.error.main,
        },

        title: {
            fontSize: 17,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
        },

        subtitle: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            maxWidth: 300,
        },

        itemChip: {
            display: 'inline-block',
            backgroundColor: theme.palette.grey[100],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            padding: theme.spacing(0.75, 1.5),
            fontSize: 12,
            fontWeight: 600,
            color: theme.palette.text.primary,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' as const,
        },

        warningBanner: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            backgroundColor: '#fff8f0',
            border: `1px solid #ffe0b2`,
            borderRadius: 8,
            padding: theme.spacing(1, 1.5),
            width: '100%',
            boxSizing: 'border-box' as const,
        },

        warningIcon: {
            fontSize: 16,
            color: '#e65100',
            flexShrink: 0,
        },

        warningText: {
            fontSize: 12,
            color: '#bf360c',
            textAlign: 'left' as const,
        },

        actions: {
            padding: theme.spacing(2, 3, 3),
            display: 'flex',
            flexDirection: 'column' as const,
            gap: theme.spacing(1),
        },

        deleteBtn: {
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 10,
            boxShadow: 'none',
            padding: theme.spacing(1.25, 0),
            backgroundColor: theme.palette.error.main,
            color: '#fff',
            '&:hover': {
                backgroundColor: theme.palette.error.dark,
                boxShadow: 'none',
            },
        },

        cancelBtn: {
            fontSize: 13,
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: 10,
            padding: theme.spacing(1.25, 0),
            color: theme.palette.text.secondary,
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[300]}`,
            },
        },
    })
);

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    itemType = 'item',
    itemName,
    title,
    description,
    confirmLabel,
}) => {
    const classes = useStyles();

    const resolvedTitle    = title       ?? `Delete this ${itemType}?`;
    const resolvedDesc     = description ?? `You're about to permanently delete this ${itemType}. This cannot be recovered once deleted.`;
    const resolvedConfirm  = confirmLabel ?? `Yes, delete ${itemType}`;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{ className: classes.paper }}
        >
            {/* Close button row */}
            <div className={classes.header}>
                <IconButton className={classes.closeBtn} size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>

            {/* Main content */}
            <DialogContent className={classes.content}>
                <div className={classes.iconWrapper}>
                    <DeleteOutlineIcon className={classes.icon} />
                </div>

                <Typography className={classes.title}>
                    {resolvedTitle}
                </Typography>

                <Typography className={classes.subtitle}>
                    {resolvedDesc}
                </Typography>

                {itemName && (
                    <span className={classes.itemChip} title={itemName}>
                        {itemName}
                    </span>
                )}

                <div className={classes.warningBanner}>
                    <ErrorOutlineIcon className={classes.warningIcon} />
                    <Typography className={classes.warningText}>
                        This action is permanent and cannot be undone.
                    </Typography>
                </div>
            </DialogContent>

            {/* Actions */}
            <DialogActions className={classes.actions} disableSpacing>
                <Button
                    className={classes.deleteBtn}
                    variant="contained"
                    fullWidth
                    startIcon={<DeleteOutlineIcon />}
                    onClick={onConfirm}
                >
                    {resolvedConfirm}
                </Button>
                <Button
                    className={classes.cancelBtn}
                    variant="outlined"
                    fullWidth
                    onClick={onClose}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmationDialog;